import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  Toolbar,
  Typography,
  Button,
  Tooltip,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { green, red } from "@mui/material/colors";
import { getAccessJwtToken } from "../../store/globalUtilities";
import CmsContext from "../../store/cms-context";

const API_URL =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/templates";

// Names that cannot be deleted (only edited)
const PROTECTED_TEMPLATE_NAMES = new Set([
  "onboarding_welcome_flow",
  "utility_optout",
]);

const getId = (t) => {
  const v = t.template_id ?? t.id;   // prefer template_id, fallback to id
  return v == null ? null : String(v);
};

function isProtectedTemplate(t) {
  return PROTECTED_TEMPLATE_NAMES.has(
    (t?.template_name ?? "")
      .toString()
      .trim()
      .toLowerCase()
  );
}

function WhatsAppTemplatesTable({
  all_templates,
  delete_templates,
  test_template,
  set_dialog_template,
}) {
  const cmsContext = useContext(CmsContext);
  const isMountedRef = useRef(false);

  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch on mount, update local copy
  const fetchTemplates = async () => {
    if (!isMountedRef.current) return;
    try {
      const auth = await getAccessJwtToken();
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.jwt,
          email: cmsContext.current_shop.email,
        },
      });
      const data = await res.json();
      if (isMountedRef.current) {
        if (Array.isArray(data.templates)) {
          setTemplates(data.templates);
        } else {
          cmsContext.setSnackBarOpen(true, "error", "Failed to load templates.");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error("Error fetching templates:", err);
        cmsContext.setSnackBarOpen(true, "error", "Error fetching templates.");
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchTemplates();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const filtered = useMemo(
    () =>
      templates.filter((t) =>
        (t.template_name || "").toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [templates, searchQuery]
  );

  const selected = filtered
    .filter((t) => {
      const id = getId(t);
      return id && selectedIds.includes(id);
    })
    .map((t) => {
      const id = getId(t);
      return { ...t, id, template_id: id }; // ensure both fields present
    });

  const hasProtectedSelected = selected.some(isProtectedTemplate);
  const canDelete = selected.length > 0 && !hasProtectedSelected;

 const handleSelectAll = (e) =>
  setSelectedIds(e.target.checked ? filtered.map(getId).filter(Boolean) : []);

  const handleSelectOne = (rawId) => {
    const id = rawId == null ? null : String(rawId);
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const doDelete = () => {
    if (!canDelete) {
      if (hasProtectedSelected) {
        cmsContext.setSnackBarOpen(
          true,
          "warning",
          "‘onboarding_welcome_flow’ and ‘opt_out_template’ cannot be deleted — only edited."
        );
      }
      return;
    }
    delete_templates(selected);
    setSelectedIds([]);
  };

  const doTest = () => {
    if (selected.length === 1) {
      const s = selected[0];
      // ensure 'id' is present (use template_id or id fallback)
      test_template({ ...s, id: (s.template_id ?? s.id)?.toString() });
    }
  };

  const doEdit = () => {
    if (selected.length === 1) set_dialog_template(selected[0]);
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Paper sx={{ mb: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h6">
            Templates
          </Typography>
          <TextField
            size="small"
            placeholder="Template Names…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mr: 2 }}
          />
          <Button onClick={doEdit} disabled={selected.length !== 1} sx={{ mr: 1 }}>
            Edit
          </Button>
          <Button
            onClick={doTest}
            disabled={selected.length !== 1}
            startIcon={<RefreshIcon />}
            sx={{ mr: 1 }}
          >
            Test
          </Button>

          <Tooltip
            title={
              hasProtectedSelected
                ? "Delete disabled: protected templates selected"
                : "Delete"
            }
          >
            <span>
              <IconButton onClick={doDelete} disabled={!canDelete}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 && selected.length === filtered.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Campaign URL</TableCell>
                <TableCell>UTM Source</TableCell>
                <TableCell>UTM Campaign</TableCell>
                <TableCell>Name Enabled</TableCell>
                <TableCell>Tested</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((t) => {
                const id = getId(t);
                const isSelected = !!id && selectedIds.includes(id);
                const protectedRow = isProtectedTemplate(t);
                return (
                  <TableRow
                    hover
                    key={id || t.template_name}
                    selected={isSelected}
                    onClick={() => handleSelectOne(id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} />
                    </TableCell>
                      <TableCell>
                        {/\.(mp4)(\?.*)?$/i.test(t.image_url || "") ? (
                          <video
                            src={t.image_url}
                            style={{ width: 50, height: 50, objectFit: "cover" }}
                            muted
                            loop
                            playsInline
                          />
                        ) : (
                          <img
                            src={t.image_url}
                            alt=""
                            style={{ width: 50, height: 50, objectFit: "cover" }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {t.template_name}
                        {protectedRow && (
                          <Chip size="small" label="Protected" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{t.campaign_url}</TableCell>
                      <TableCell>{t.utm_source}</TableCell>
                      <TableCell>{t.utm_campaign}</TableCell>
                      <TableCell align="center">
                        {t.first_name_enabled ? (
                          <CheckIcon/>
                        ) : (
                          <CloseIcon/>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.tested ? "Tested" : "Untested"}
                          sx={{
                            backgroundColor: t.tested ? green[500] : red[500],
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, np) => setPage(np)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
}

export default React.memo(WhatsAppTemplatesTable);
